import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Section, TextField, TextArea, Toggle, AdminButton } from "@/components/admin/AdminUI";
import MediaUploader from "@/components/admin/MediaUploader";
import { Pencil, Trash2, Plus } from "lucide-react";

const empty = { title: "", description: "", image_url: "", image_public_id: null, featured: false, display_order: 0 };

export default function MoodboardsAdmin() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/moodboards").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      if (editing.id) await api.put(`/moodboards/${editing.id}`, editing);
      else await api.post("/moodboards", editing);
      setEditing(null);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    await api.delete(`/moodboards/${m.id}`);
    load();
  };

  return (
    <AdminLayout>
      <Section title="Moodboards" actions={<AdminButton testId="new-moodboard" onClick={() => setEditing({ ...empty })}><Plus size={14} /> New Moodboard</AdminButton>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {list.map((m) => (
            <div key={m.id} data-testid={`row-moodboard-${m.id}`} className="border border-[color:var(--border)] bg-[color:var(--surface)]">
              <div className="aspect-square"><img src={m.image_url} className="w-full h-full object-cover" /></div>
              <div className="p-3">
                <div className="font-display italic text-lg">{m.title} {m.featured && "★"}</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1 line-clamp-2">{m.description}</div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`edit-moodboard-${m.id}`} onClick={() => setEditing({ ...empty, ...m })} className="eyebrow link-underline">Edit</button>
                  <button data-testid={`delete-moodboard-${m.id}`} onClick={() => del(m)} className="eyebrow text-red-400">Delete</button>
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="col-span-full text-center py-10 text-[color:var(--text-muted)]">No moodboards yet.</div>}
        </div>
      </Section>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4 md:p-10" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <form onSubmit={save} className="max-w-xl mx-auto bg-[color:var(--surface)] border border-[color:var(--border)] p-6 md:p-10">
            <h3 className="font-display italic text-3xl mb-6">{editing.id ? "Edit Moodboard" : "New Moodboard"}</h3>
            <div className="mb-4"><TextField testId="mb-title" label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></div>
            <div className="mb-4"><TextArea testId="mb-desc" label="Short description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={2} /></div>
            <div className="mb-6">
              <div className="eyebrow mb-2">Moodboard image</div>
              <MediaUploader testId="mb-image-upload"
                value={{ url: editing.image_url, public_id: editing.image_public_id, storage_key: editing.image_storage_key }}
                onChange={(v) => setEditing({ ...editing, image_url: v.url, image_public_id: v.public_id, image_storage_key: v.storage_key })}
                folder="moodboards" purpose="moodboard_image" label="Upload moodboard image" />
            </div>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <TextField testId="mb-order" label="Display order" type="number" value={editing.display_order} onChange={(v) => setEditing({ ...editing, display_order: parseInt(v) || 0 })} />
              <Toggle testId="mb-featured" label="Featured" value={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} />
            </div>
            {err && <div className="text-red-400 text-sm mb-4">{err}</div>}
            <div className="flex justify-end gap-3">
              <AdminButton variant="ghost" onClick={() => setEditing(null)}>Cancel</AdminButton>
              <AdminButton type="submit" testId="mb-save" disabled={saving}>{saving ? "Saving…" : "Save"}</AdminButton>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
